import { PauseIcon, PlayIcon } from '@/components/Icons';
import Image from '@/components/Image';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MarketAudioDto } from '@/service/type';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import { cn, titleCaseToKebabCase } from '@/utils';
import { ArrowUpDown } from 'lucide-react';
import { CSSProperties, useMemo, useState } from 'react';
import { Marquee } from './Marquee';
import { useDimensions } from '@/hooks/useDimensions';
import { Pagination } from './Pagination';
import { useNavigate } from 'react-router-dom';

interface TrackListProps {
  trackListData?: MarketAudioDto[];
  style?: {
    contentHeight: CSSProperties['height'];
  };
  itemsPerPage?: number;
  count?: number;
  showSorter?: boolean;
  theme?: 'dark' | 'light';
  onPageChange?: (page: number) => void;
  label?: {
    title: string;
    creator: string;
    filter: string;
  };
  ifNavigateInFilterTag?: boolean;
  isSliceData?: boolean;
}

export function TrackList({
  trackListData,
  style,
  itemsPerPage = 10,
  count,
  showSorter = true,
  theme = 'light',
  onPageChange,
  label = {
    title: 'Title',
    creator: 'Artist',
    filter: 'StyleFilter',
  },
  ifNavigateInFilterTag = true,
  isSliceData = true,
}: TrackListProps) {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc' | null;
  }>({
    key: label.title,
    direction: null,
  });

  const { isMobileOrPad } = useDimensions();

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedData = useMemo(() => {
    if (!trackListData) return [];

    const sorted = [...trackListData];
    if (sortConfig.direction === null) return sorted;

    return sorted.sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (sortConfig.key) {
        case label.title:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case label.creator:
          aValue = (a.user?.name || a.user_name || '').toLowerCase();
          bValue = (b.user?.name || b.user_name || '').toLowerCase();
          break;
        case label.filter:
          aValue = a.stylefilter && 'name' in a.stylefilter ? a.stylefilter.name : '';
          bValue = b.stylefilter && 'name' in b.stylefilter ? b.stylefilter.name : '';
          break;
        default:
          return 0;
      }

      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [trackListData, sortConfig]);

  const [currentPage, setCurrentPage] = useState(1);
  // const itemsPerPage = 10;

  const paginatedData = useMemo(() => {
    if (isSliceData) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      return sortedData.slice(startIndex, startIndex + itemsPerPage);
    }
    return sortedData;
  }, [sortedData, currentPage, isSliceData]);

  const totalPages = Math.ceil(count ? count / itemsPerPage : sortedData.length / itemsPerPage);

  const listKey = `${sortConfig.key}-${sortConfig.direction}-${currentPage}`;

  if (!trackListData) {
    return null;
  }

  if (isMobileOrPad) {
    return (
      <div>
        <div>
          <div
            className={cn(
              'table_header border-b pb-4 grid grid-cols-6 gap-6 text-jen-interactive-tertiary text-[16px]',
              theme === 'dark' ? 'text-[#71717A] border-[#242425]' : 'text-[#111112] border-jen-border-primary',
            )}
          >
            <div className="col-span-3 cursor-pointer text-[#71717a]" onClick={() => handleSort(label.title)}>
              <ArrowUpDown className="w-[16px] inline" />
              <span className="ml-2">Title</span>
            </div>
          </div>
          <ScrollArea
            key={listKey}
            className="pr-2"
            style={{
              height: style?.contentHeight,
            }}
          >
            {paginatedData.map((audio, index) => {
              return (
                <TrackItemMobile
                  ifNavigateInFilterTag={ifNavigateInFilterTag}
                  key={audio.id}
                  audio={audio}
                  className={cn('mb-4', { 'mt-4': index === 0 })}
                  index={index}
                  theme={theme}
                />
              );
            })}
          </ScrollArea>
          {paginatedData?.length && (
            <div className="mt-6 mb-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(val) => {
                  setCurrentPage(val);
                  onPageChange?.(val);
                }}
                theme={theme}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-hidden">
        {showSorter && (
          <div className="table_header border-b border-jen-border-primary pb-4 grid grid-cols-[1fr_150px_180px] gap-6 text-jen-interactive-tertiary text-[16px]">
            <div className="cursor-pointer text-[#71717a]" onClick={() => handleSort(label.title)}>
              <ArrowUpDown className="w-[16px] inline" />
              <span className="ml-2">{label.title}</span>
            </div>
            <div className="cursor-pointer text-[#71717a]" onClick={() => handleSort(label.creator)}>
              <ArrowUpDown className="w-[16px] inline" />
              <span className="ml-2">{label.creator}</span>
            </div>
            <div className="cursor-pointer text-[#71717a]" onClick={() => handleSort(label.filter)}>
              <ArrowUpDown className="w-[16px] inline" />
              <span className="ml-2">{label.filter}</span>
            </div>
          </div>
        )}
        <ScrollArea
          key={listKey}
          className=" overflow-y-auto pt-4 pr-2"
          style={{
            height: style?.contentHeight,
          }}
        >
          {paginatedData.map((audio, index) => {
            return (
              <TrackItem
                theme={theme}
                ifNavigateInFilterTag={ifNavigateInFilterTag}
                key={audio.id}
                audio={audio}
                className={cn('mb-[16px]', { 'mt-4': index === 0 })}
                index={index}
              />
            );
          })}
        </ScrollArea>
        {paginatedData?.length && (
          <div className="mt-6 mb-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(val) => {
                setCurrentPage(val);
                onPageChange?.(val);
              }}
              theme={theme}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface TrackItemProps {
  className?: string;
  audio: MarketAudioDto;
  index: number;
  theme?: 'dark' | 'light';
  ifNavigateInFilterTag: boolean;
}

const TrackItem = ({ className, audio, index, theme, ifNavigateInFilterTag }: TrackItemProps) => {
  const { isPlaying, playAudioInfo, pause, play } = useAudioPlayerStore();
  const navigate = useNavigate();

  const isCurrentPlaying = String(playAudioInfo?.id) === String(audio.id) && isPlaying;

  return (
    <div className="grid grid-cols-[1fr_150px_180px] gap-6 mb-4 h-[44px] shrink-0">
      <div className="flex items-center h-full gap-[15px] min-w-0">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-jen-interactive-primary flex items-center justify-center">
          <div
            className="flex  gap-2 text-jen-interactive-primary items-center cursor-pointer hover:opacity-90"
            onClick={() => (isCurrentPlaying ? pause() : play(audio as any))}
          >
            <div
              className="flex justify-center items-center w-9 h-9 rounded-full"
              style={{
                background: ' var(--Colors-Orange-10, rgba(255, 91, 39, 0.10))',
              }}
            >
              {isCurrentPlaying ? <PauseIcon width={16} fillColor="#fff" /> : <PlayIcon width={16} fillColor="#fff" />}
            </div>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <Marquee className="text-[1.2rem] truncate">{audio.name}</Marquee>
        </div>
      </div>
      <div className="flex items-center truncate text-[#71717A]">{audio.user?.name || audio.user_name}</div>
      <div className="w-[140px] flex-shrink-0">
        {typeof audio.stylefilter === 'object' && !!audio.stylefilter && 'image' in audio.stylefilter && (
          <div
            className={cn('flex w-full h-full', {
              'cursor-pointer': ifNavigateInFilterTag && audio?.stylefilter?.stylefilter_name,
            })}
            id={index === 0 ? 'stylefilter' : ''}
            onClick={() =>
              ifNavigateInFilterTag &&
              audio?.stylefilter?.stylefilter_name &&
              navigate(`/marketplace/filter/${titleCaseToKebabCase(audio?.stylefilter?.stylefilter_name)}`)
            }
          >
            <span
              className={cn(
                'bg-[#353535] p-1 flex flex-row gap-[10px] items-center rounded-[6px] styleFilterShadow w-full',
                theme === 'dark' ? 'bg-[#3D3D3F]' : 'bg-[#353535]',
              )}
            >
              <Image
                src={audio.stylefilter.image}
                alt={audio.stylefilter.name}
                width={40}
                quality={100}
                className="rounded w-6 h-full flex-shrink-0"
              />
              <div className="flex flex-col flex-1 min-w-0 text-white ">
                <span className="relative uppercase font-secondaryMono text-[9px]">
                  Stylefilter
                  <span className="absolute top-0 ml-[2px] text-[6px]">TM</span>
                </span>
                <span className="text-[0.8rem] leading-[100%]  flex gap-1 overflow-hidden">
                  <span className="truncate text-[#fff] whitespace-nowrap overflow-hidden">{audio.stylefilter.artist}</span>
                </span>
              </div>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const TrackItemMobile = ({ className, audio, index, theme, ifNavigateInFilterTag }: TrackItemProps) => {
  const { isPlaying, playAudioInfo, pause, play } = useAudioPlayerStore();
  const navigate = useNavigate();

  const isCurrentPlaying = String(playAudioInfo?.id) === String(audio.id) && isPlaying;

  return (
    <div className={cn('flex items-center gap-3 w-full h-[44px]', className)}>
      <button
        onClick={() => (isCurrentPlaying ? pause() : play(audio as any))}
        className={cn('w-9 h-9 rounded-full bg-[#FF5B27] flex items-center justify-center flex-shrink-0')}
      >
        {isCurrentPlaying ? <PauseIcon width={20} fillColor="#fff" /> : <PlayIcon width={20} fillColor="#fff" />}
      </button>

      <div className="flex-grow min-w-0">
        <h3 className={cn('text-[14px] font-medium  line-clamp-1 leading-[14px]', theme === 'dark' ? 'text-white' : 'text-[#111112]')}>
          {audio.name}
        </h3>
        <p className={cn('text-[12px] text-[#71717A] mt-0.5 line-clamp-1 leading-[14px]')}>{audio.user?.name}</p>
      </div>

      {typeof audio.stylefilter === 'object' && audio.stylefilter && 'image' in audio.stylefilter && (
        <div
          className="w-6 h-[38px] flex-shrink-0 rounded-lg overflow-hidden cursor-pointer"
          onClick={() =>
            ifNavigateInFilterTag &&
            audio?.stylefilter?.stylefilter_name &&
            navigate(`/marketplace/filter/${titleCaseToKebabCase(audio?.stylefilter?.stylefilter_name)}`)
          }
        >
          <Image
            src={audio.stylefilter.image || '/placeholder.svg'}
            alt={audio.stylefilter.name || 'Track cover'}
            width={24}
            height={38}
            quality={100}
            className="bg-gray-500 w-full h-full object-cover"
          />
        </div>
      )}
    </div>
  );
};
